/**
 * <venner-engraving>
 *
 * Initials field for engraving. Replaces the personalization app.
 *
 * Responsibilities:
 *   - Show or hide the field based on the selected variant (Prestige's `variant:change` event).
 *   - Force uppercase and filter out non-engravable characters while the customer types.
 *   - Block checkout (including the dynamic checkout button) if the initials are missing.
 *   - Allow opting out of engraving on a specific item (bundles).
 *
 * When the field is hidden or the customer checks "no engraving", the input is disabled: that way the
 * property isn't sent to the cart and it doesn't block the form's native validation either.
 */
if (!window.customElements.get('venner-engraving')) {
  const ALLOWED_CHARACTERS = /[^A-Z0-9Ñ]/g;

  class VennerEngraving extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.input = this.querySelector('[data-engraving-input]');
      this.errorMessage = this.querySelector('[data-engraving-error]') || this.querySelector('.venner-engraving__error');
      this.optOutToggle = this.querySelector('[data-engraving-opt-out-toggle]');
      this.optOutValue = this.querySelector('[data-engraving-opt-out-value]');
      this.helpButton = this.querySelector('[data-engraving-help]');
      this.helpWrap = this.querySelector('.venner-engraving__help-wrap');
      this.tooltip = this.querySelector('.venner-engraving__tooltip');

      if (!this.input) {
        return;
      }

      this.input.addEventListener('input', this.onInput.bind(this), { signal });
      this.input.addEventListener('invalid', this.onInvalid.bind(this), { signal });
      this.input.addEventListener('blur', this.refreshValidity.bind(this), { signal });

      if (this.optOutToggle) {
        this.optOutToggle.addEventListener('change', this.onOptOutChange.bind(this), { signal });
      }

      if (this.helpButton) {
        this.helpButton.addEventListener('click', this.toggleTooltip.bind(this), { signal });
        document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
        document.addEventListener('keydown', this.onDocumentKeydown.bind(this), { signal });

        // Hover and focus open the tooltip from the CSS alone, so there's no single moment to
        // hook into: it's measured right before each of the ways it can show up, and again when
        // the viewport changes size.
        const place = this.placeTooltip.bind(this);

        this.helpWrap?.addEventListener('pointerenter', place, { signal });
        this.helpButton.addEventListener('focus', place, { signal });
        window.addEventListener('resize', place, { passive: true, signal });
      }

      // The form is declared further down in the DOM (it lives inside buy_buttons), so it doesn't exist yet
      // when connectedCallback runs. `variant:change` bubbles and carries the form id in its detail, so
      // listening on document avoids depending on block order.
      document.addEventListener('variant:change', this.onVariantChange.bind(this), { signal });

      // Safety net. The field's state decides whether initials make it onto the order or not, so it can't
      // depend on an event having arrived correctly: right before submitting, the variant the server has
      // rendered is read again and the state is recalculated. It runs in the capture phase to get ahead of
      // Prestige's <product-form>, which builds the FormData and sends it to the cart as soon as it gets
      // the submit.
      document.addEventListener('submit', this.onFormSubmit.bind(this), { capture: true, signal });

      // The dynamic checkout button (PayPal, Shop Pay…) doesn't trigger the form's native validation, so we
      // intercept it in the capture phase before Shopify processes it.
      document.addEventListener('click', this.onPaymentButtonClick.bind(this), { capture: true, signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * The input itself is asked for its form: `input.form` is the association the browser makes from the
     * `form=` attribute, and it's more reliable than looking it up by hand in `document.forms`.
     */
    get form() {
      return this.input?.form || document.forms[this.getAttribute('form')] || null;
    }

    get maxLength() {
      return parseInt(this.getAttribute('max-length'), 10) || 0;
    }

    get visibility() {
      return this.getAttribute('visibility') || 'variant';
    }

    get triggerValues() {
      return (this.getAttribute('trigger-values') || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    }

    get isRequired() {
      return this.hasAttribute('data-required');
    }

    get isOptedOut() {
      return Boolean(this.optOutToggle?.checked);
    }

    /**
     * Normalizes what the customer types: uppercase, no accents, letters and numbers only.
     * Keeps the cursor position so a typo can be fixed mid-word.
     */
    onInput() {
      const cursorPosition = this.input.selectionStart;
      const previousValue = this.input.value;
      const nextValue = previousValue
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u0302\u0304-\u0308\u030A-\u036F]/g, '') // keeps the tilde on Ñ (U+0303)
        .normalize('NFC')
        .replace(ALLOWED_CHARACTERS, '');

      if (nextValue !== previousValue) {
        this.input.value = nextValue;
        const offset = previousValue.length - nextValue.length;
        this.input.setSelectionRange(Math.max(0, cursorPosition - offset), Math.max(0, cursorPosition - offset));
      }

      this.refreshValidity();
    }

    onInvalid(event) {
      // We cancel the browser's native bubble ("Fill out this field"): it covers the field and repeats what
      // the red message below already says. The field is still invalid, so the form still can't be
      // submitted.
      event.preventDefault();

      this.errorMessage?.removeAttribute('hidden');
      this.input.setAttribute('aria-invalid', 'true');

      // With the bubble canceled, the browser doesn't move focus to the field either, so we do it. If
      // another invalid field is already focused we leave it alone: sets have several items with
      // initials, and the last one to react would steal focus from the first.
      const focused = document.activeElement;

      if (focused !== this.input && focused?.matches?.('input:invalid, select:invalid, textarea:invalid')) {
        return;
      }

      this.input.focus();
    }

    refreshValidity() {
      if (this.input.validity.valid) {
        this.errorMessage?.setAttribute('hidden', '');
        this.input.removeAttribute('aria-invalid');
      }
    }

    onOptOutChange() {
      const optedOut = this.isOptedOut;

      this.input.disabled = optedOut;
      this.input.required = !optedOut && this.isRequired;

      if (optedOut) {
        this.input.value = '';
        this.refreshValidity();
      }

      if (this.optOutValue) {
        this.optOutValue.disabled = !optedOut;
      }

      this.classList.toggle('venner-engraving--opted-out', optedOut);
    }

    /**
     * Automatic mode needs no configuration: engraved variants are named "With engraving" and the others
     * "Without engraving". The negative one is still ruled out first, so a variant named "Without
     * engraving" can never turn the field on.
     */
    matchesVariant(variantTitle) {
      if (this.visibility === 'auto') {
        return !variantTitle.includes('without engraving') && variantTitle.includes('with engraving');
      }

      return this.triggerValues.some((triggerValue) => variantTitle.includes(triggerValue));
    }

    onVariantChange() {
      // No filtering by form: `sync()` reads the variant from THIS field's picker, so an event from another
      // product on the page can't affect it. Filtering here only added a way to get stuck if the comparison
      // failed.
      this.sync();
    }

    onFormSubmit() {
      // No filtering by form here either, for the same reason: re-syncing too often breaks nothing, while
      // re-syncing too little sends initials to the order that the customer didn't ask for.
      this.sync();
    }

    /**
     * The truth about which variant is selected doesn't come from the event, but from the JSON that
     * Prestige re-renders inside the variant picker on every change. Reading it from there keeps the field
     * correct even if no event arrived.
     */
    selectedVariantTitle() {
      const formId = this.getAttribute('form');
      const script = document.querySelector(`variant-picker[form-id="${formId}"] script[data-variant]`);

      if (!script) {
        return null;
      }

      try {
        return (JSON.parse(script.textContent)?.title || '').toLowerCase();
      } catch (error) {
        return null;
      }
    }

    sync() {
      if (this.visibility === 'always') {
        return;
      }

      if (this.visibility === 'variant' && this.triggerValues.length === 0) {
        return;
      }

      const variantTitle = this.selectedVariantTitle();

      if (variantTitle === null) {
        return;
      }

      const isActive = this.matchesVariant(variantTitle);

      this.hidden = !isActive;
      this.input.disabled = !isActive || this.isOptedOut;
      this.input.required = isActive && !this.isOptedOut && this.isRequired;

      if (this.optOutValue) {
        this.optOutValue.disabled = !isActive || !this.isOptedOut;
      }

      if (!isActive) {
        // Cleared as well as disabled: if the field somehow got submitted anyway, it shouldn't carry
        // initials the customer already discarded.
        this.input.value = '';
        this.refreshValidity();
      }
    }

    onPaymentButtonClick(event) {
      const paymentButton = event.target.closest?.('.shopify-payment-button');

      if (!paymentButton) {
        return;
      }

      const form = this.form;

      if (form?.contains(paymentButton)) {
        // Express checkout skips the submit, so we also have to re-sync here before validating.
        this.sync();
      }

      if (form?.contains(paymentButton) && !form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        form.reportValidity();
      }
    }

    /**
     * Hover and focus are handled by the CSS. This only covers taps on mobile, where there's no hover.
     */
    toggleTooltip() {
      this.placeTooltip();
      this.helpWrap?.toggleAttribute('data-open');
    }

    /**
     * The tooltip is centered on the "?" icon, which sits near the start of the row, so on a
     * phone it runs off the edge of the screen and the first words get cut. It's nudged back
     * inside the viewport; the arrow moves the opposite way from the CSS, so it still points at
     * the icon. Measuring works while the tooltip is only `visibility: hidden` — it's laid out.
     */
    placeTooltip() {
      if (!this.tooltip) return;

      this.tooltip.style.setProperty('--engraving-tooltip-shift', '0px');

      const gutter = 12;
      const box = this.tooltip.getBoundingClientRect();
      let shift = 0;

      if (box.left < gutter) {
        shift = gutter - box.left;
      } else if (box.right > window.innerWidth - gutter) {
        shift = window.innerWidth - gutter - box.right;
      }

      this.tooltip.style.setProperty('--engraving-tooltip-shift', `${Math.round(shift)}px`);
    }

    closeTooltip() {
      this.helpWrap?.removeAttribute('data-open');
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeTooltip();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closeTooltip();
      }
    }
  }

  window.customElements.define('venner-engraving', VennerEngraving);
}
